import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AdobeIcon from 'app/assets/icons/apps_icons/AdobeIcon.svg';
import { DateTooltip } from 'app/components/TimelineCard/DateTooltip';
import timelineCardStyles from 'app/components/TimelineCard/TimelineCard.module.scss';
import { useHomeSlice } from 'app/pages/Home/slice';
import { selectParkingLot } from 'app/pages/Home/slice/selectors';
import { getRtmPhasedTitleParts } from 'utils/rtmPhaseDisplayTitle';
import { calculateMonthlyTooltipPosition } from '../utils/calculateMonthlyTooltipPosition';
import { isMonthlyItemOverdue } from '../utils/getMonthlyBadgeVariant';
import { isItemClosed } from '../utils/monthlyItemIconUtils';
import type { MonthlyTimelineItem } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyDateQuickEditHostProps {
  item: MonthlyTimelineItem;
  laneLabel: string;
  children: React.ReactNode;
  isDateClicked?: boolean;
  setIsDateClicked?: (isDateClicked: boolean) => void;
}

export const MonthlyDateQuickEditHost = memo(
  ({
    item,
    laneLabel,
    children,
    isDateClicked: isDateClickedProp,
    setIsDateClicked: setIsDateClickedProp
  }: MonthlyDateQuickEditHostProps) => {
    const { actions } = useHomeSlice();
    const dispatch = useDispatch();
    const showParkingLot = useSelector(selectParkingLot);

    const [internalIsDateClicked, setInternalIsDateClicked] = useState(false);
    const isDateClicked = isDateClickedProp ?? internalIsDateClicked;
    const setIsDateClicked = useCallback(
      (value: boolean) => {
        if (setIsDateClickedProp) {
          setIsDateClickedProp(value);
        } else {
          setInternalIsDateClicked(value);
        }
      },
      [setIsDateClickedProp]
    );

    const [tooltipPlacement, setTooltipPlacement] = useState<{
      vertical: 'top' | 'bottom';
      horizontal: 'left' | 'center' | 'right';
    }>({ vertical: 'top', horizontal: 'left' });
    const [isHoverPreview, setIsHoverPreview] = useState(false);

    const dateContainerRef = useRef<HTMLDivElement>(null);
    const dateTooltipRef = useRef<HTMLDivElement>(null);
    const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hoverPreviewTimerRef = useRef<NodeJS.Timeout | null>(null);

    const wholeCard = useMemo(
      () => ({
        ...item,
        status: item.status || ''
      }),
      [item]
    );
    const issueId = item.issueId || '';
    const projectType = item.projectType || item.type || '';
    const title = item.title || '';
    const isClosed = isItemClosed(item);
    const isOverdue = isMonthlyItemOverdue(item, laneLabel);

    const titleParts = useMemo(
      () =>
        getRtmPhasedTitleParts({
          momentType: projectType,
          rawTitle: title,
          releasePhase: item.releasePhase,
          releasePhaseId: item.releasePhaseId,
          releasePhases: Array.isArray(item.releasePhases)
            ? item.releasePhases
            : null
        }),
      [
        item.releasePhase,
        item.releasePhaseId,
        item.releasePhases,
        projectType,
        title
      ]
    );

    const products = useMemo(() => {
      if (Array.isArray(item.products)) {
        return item.products;
      }

      if (typeof item.products === 'string' && item.products.trim()) {
        return [item.products];
      }

      return [];
    }, [item.products]);

    const categories = useMemo(() => {
      if (Array.isArray(wholeCard.categories)) {
        return wholeCard.categories;
      }

      if (
        typeof wholeCard.categories === 'string' &&
        wholeCard.categories.trim()
      ) {
        return [wholeCard.categories];
      }

      return [];
    }, [wholeCard.categories]);

    const updateTooltipPlacement = useCallback(() => {
      const position = calculateMonthlyTooltipPosition(
        dateContainerRef.current,
        252,
        600,
        showParkingLot
      );
      setTooltipPlacement(position);
    }, [showParkingLot]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          isDateClicked &&
          dateContainerRef.current &&
          dateTooltipRef.current &&
          !dateContainerRef.current.contains(event.target as Node) &&
          !dateTooltipRef.current.contains(event.target as Node)
        ) {
          setIsDateClicked(false);
          dispatch(actions.resetTooltipDetails());
        }
      };

      if (isDateClicked) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [actions, dispatch, isDateClicked, setIsDateClicked]);

    useEffect(() => {
      return () => {
        if (tooltipTimerRef.current) {
          clearTimeout(tooltipTimerRef.current);
        }
        if (hoverPreviewTimerRef.current) {
          clearTimeout(hoverPreviewTimerRef.current);
        }
      };
    }, []);

    const showTooltipPreview = isDateClicked || isHoverPreview;

    const handleTooltipMouseEnter = useCallback((_key: string) => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    }, []);

    const handleTooltipMouseLeave = useCallback(() => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    }, []);

    const handleDateMouseEnter = useCallback(() => {
      updateTooltipPlacement();

      if (isDateClicked) return;

      if (hoverPreviewTimerRef.current) {
        clearTimeout(hoverPreviewTimerRef.current);
      }

      hoverPreviewTimerRef.current = setTimeout(() => {
        setIsHoverPreview(true);
      }, 1000);
    }, [isDateClicked, updateTooltipPlacement]);

    const handleDateMouseLeave = useCallback(() => {
      if (hoverPreviewTimerRef.current) {
        clearTimeout(hoverPreviewTimerRef.current);
      }
      setIsHoverPreview(false);
      handleTooltipMouseLeave();
    }, [handleTooltipMouseLeave]);

    const handleImageError = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.src = AdobeIcon;
      },
      []
    );

    const handleDateClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setIsHoverPreview(false);
        if (hoverPreviewTimerRef.current) {
          clearTimeout(hoverPreviewTimerRef.current);
        }
        updateTooltipPlacement();
        setIsDateClicked(!isDateClicked);
      },
      [isDateClicked, setIsDateClicked, updateTooltipPlacement]
    );

    if (!issueId) {
      return <>{children}</>;
    }

    return (
      <div
        ref={dateContainerRef}
        className={`${styles.monthlyDateQuickEditContainer} ${
          isDateClicked ? timelineCardStyles.dateClicked : ''
        } ${isDateClicked ? styles.monthlyDateQuickEditActive : ''}`}
        onClick={handleDateClick}
        onMouseEnter={handleDateMouseEnter}
        onMouseLeave={handleDateMouseLeave}
        data-testid={`monthly-date-quick-edit-${issueId}`}
      >
        {children}
        {showTooltipPreview && (
          <DateTooltip
            isDateClicked={isDateClicked}
            setIsDateClicked={setIsDateClicked}
            dateTooltipRef={dateTooltipRef}
            categories={categories}
            products={products}
            title={titleParts.displayTitle}
            storageTitle={titleParts.storageTitle}
            phaseTitleLabel={titleParts.phaseLabel || undefined}
            isClosed={isClosed}
            riskLevel={item.riskLevel}
            issueId={issueId}
            getStatusIcon={() => null}
            handleTooltipMouseEnter={handleTooltipMouseEnter}
            handleTooltipMouseLeave={handleTooltipMouseLeave}
            lastUpdated={wholeCard.lastUpdated}
            reportingSummary={wholeCard.reportingSummary}
            handleImageError={handleImageError}
            wholeCard={wholeCard}
            tooltipPlacement={tooltipPlacement}
            isOverdue={isOverdue}
          />
        )}
      </div>
    );
  }
);
